// /admin/leads — Internal lead analytics dashboard.
// Password-gated. Shows clicks-per-stay, source breakdown, UTM attribution,
// and the most recent 200 clicks.

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;
const pf = { fontFamily: "'Playfair Display', Georgia, serif" };

const Section = ({ children, className = '' }) => (
  <section className={`py-10 px-6 md:px-12 ${className}`}>
    <div className="max-w-6xl mx-auto">{children}</div>
  </section>
);

const fmtTimeAgo = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
};

// Small key/value pair used inside the enquiry cards
const Field = ({ label, value, className = '' }) => (
  <div className={className}>
    <div className="text-[9px] tracking-[0.15em] uppercase text-stone-400 font-semibold mb-0.5">{label}</div>
    <div className="text-stone-800">{value}</div>
  </div>
);

export default function AdminLeads() {
  const [password, setPassword] = useState(localStorage.getItem('gh_admin_pw') || '');
  const [authed, setAuthed] = useState(false);
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = (pw, daysVal) => {
    setLoading(true);
    fetch(`${API}/api/admin/leads?password=${encodeURIComponent(pw)}&days=${daysVal}`)
      .then((r) => r.ok ? r.json() : Promise.reject(r.status))
      .then((d) => { setData(d); setAuthed(true); localStorage.setItem('gh_admin_pw', pw); })
      .catch(() => setAuthed(false))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (password) load(password, days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (authed) load(password, days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  if (!authed) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl border border-stone-200 p-8 max-w-md w-full">
          <h1 className="text-2xl font-light italic mb-2" style={pf}>Lead Analytics</h1>
          <p className="text-sm text-stone-600 mb-6">Admin password required.</p>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load(password, days)}
            className="w-full px-4 py-3 border border-stone-300 rounded-lg mb-4"
            placeholder="Password"
            data-testid="leads-password"
          />
          <button
            onClick={() => load(password, days)}
            className="w-full bg-stone-900 text-white py-3 rounded-lg font-medium tracking-wide hover:bg-stone-800"
            data-testid="leads-signin"
          >
            {loading ? 'Loading…' : 'Sign in'}
          </button>
        </div>
      </div>
    );
  }

  const maxStayClicks = Math.max(1, ...(data?.by_stay || []).map((s) => s.clicks));
  const maxDailyClicks = Math.max(1, ...(data?.daily || []).map((d) => d.clicks));

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <div className="bg-stone-900 text-stone-100 py-10 px-6 md:px-12">
        <div className="max-w-6xl mx-auto flex justify-between items-end flex-wrap gap-3">
          <div>
            <Link to="/links" className="text-[11px] uppercase tracking-[0.3em] text-amber-200/60 hover:text-amber-200">← Links</Link>
            <h1 className="text-3xl md:text-4xl font-light italic mt-3" style={pf}>Lead Analytics</h1>
            <p className="text-stone-400 text-sm mt-2">Every WhatsApp click tracked — attribution per stay, source, and channel.</p>
          </div>
          <div className="flex gap-2">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-4 py-2 text-xs uppercase tracking-wider rounded-md ${
                  days === d ? 'bg-amber-200 text-stone-900 font-semibold' : 'bg-stone-800 text-stone-300 hover:bg-stone-700'
                }`}
                data-testid={`leads-window-${d}`}
              >Last {d}d</button>
            ))}
          </div>
        </div>
      </div>

      {/* Hero number */}
      <Section className="bg-white border-b border-stone-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-amber-900/60 mb-2">Total clicks</div>
            <div className="text-5xl md:text-6xl font-light italic text-stone-900" style={pf}>
              {data?.total ?? 0}
            </div>
            <div className="text-xs text-stone-500 mt-2">Last {data?.window_days || days} days · all sources</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-amber-900/60 mb-2">Top source</div>
            <div className="text-2xl font-light italic text-stone-900" style={pf}>
              {data?.by_source?.[0]?.source || '—'}
            </div>
            <div className="text-xs text-stone-500 mt-2">{data?.by_source?.[0]?.clicks ?? 0} clicks</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-amber-900/60 mb-2">Top stay</div>
            <div className="text-2xl font-light italic text-stone-900 truncate" style={pf}>
              {data?.by_stay?.[0]?.stay_name || '—'}
            </div>
            <div className="text-xs text-stone-500 mt-2">{data?.by_stay?.[0]?.clicks ?? 0} clicks</div>
          </div>
        </div>
      </Section>

      {/* Daily sparkbars */}
      {data?.daily?.length > 0 && (
        <Section className="bg-white border-b border-stone-200">
          <div className="text-[11px] uppercase tracking-[0.25em] text-amber-900/60 mb-4">Daily clicks</div>
          <div className="flex items-end gap-1 h-32">
            {data.daily.map((d) => (
              <div key={d.day} className="flex-1 flex flex-col items-center justify-end" title={`${d.day}: ${d.clicks} clicks`}>
                <div
                  className="w-full bg-amber-700 rounded-t hover:bg-amber-600 transition-colors"
                  style={{ height: `${(d.clicks / maxDailyClicks) * 100}%`, minHeight: '2px' }}
                />
                <div className="text-[8px] text-stone-400 mt-1 -rotate-45 origin-top-left whitespace-nowrap">
                  {d.day.slice(5)}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* By stay + by source */}
      <Section className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white border border-stone-200 rounded-xl p-6">
          <div className="text-[11px] uppercase tracking-[0.25em] text-amber-900/60 mb-4">Top stays by click volume</div>
          <div className="space-y-2.5">
            {(data?.by_stay || []).map((s) => (
              <div key={s.stay_id || s.stay_name} className="flex items-center gap-3 text-sm">
                <div className="flex-1 truncate" title={s.stay_name}>{s.stay_name}</div>
                <div className="flex-1 max-w-[150px] bg-stone-100 h-2 rounded overflow-hidden">
                  <div className="h-full bg-emerald-600 rounded" style={{ width: `${(s.clicks / maxStayClicks) * 100}%` }} />
                </div>
                <div className="font-mono text-stone-700 w-10 text-right">{s.clicks}</div>
              </div>
            ))}
            {!data?.by_stay?.length && <div className="text-sm text-stone-400 italic">No clicks yet.</div>}
          </div>
        </div>

        <div className="bg-white border border-stone-200 rounded-xl p-6">
          <div className="text-[11px] uppercase tracking-[0.25em] text-amber-900/60 mb-4">Breakdown by source / channel</div>
          <div className="mb-6">
            <div className="text-[10px] uppercase tracking-wider text-stone-500 mb-2">Source (page type)</div>
            <div className="space-y-1.5">
              {(data?.by_source || []).map((s) => (
                <div key={s.source} className="flex justify-between text-sm">
                  <span className="text-stone-700">{s.source}</span>
                  <span className="font-mono text-stone-900">{s.clicks}</span>
                </div>
              ))}
              {!data?.by_source?.length && <div className="text-xs text-stone-400 italic">—</div>}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-stone-500 mb-2">UTM Source (channel)</div>
            <div className="space-y-1.5">
              {(data?.by_utm_source || []).map((s) => (
                <div key={s.utm_source} className="flex justify-between text-sm">
                  <span className="text-stone-700">{s.utm_source}</span>
                  <span className="font-mono text-stone-900">{s.clicks}</span>
                </div>
              ))}
              {!data?.by_utm_source?.length && <div className="text-xs text-stone-400 italic">—</div>}
            </div>
          </div>
        </div>
      </Section>

      {/* Enquiries — structured leads from the EnquireModal (with dates/guests/phone) */}
      {(() => {
        const enquiries = (data?.recent || []).filter(r => r.check_in && r.check_out);
        const withPhone = enquiries.filter(r => r.guest_phone).length;
        if (enquiries.length === 0) return null;
        return (
          <Section>
            <div className="bg-white border border-stone-200 rounded-xl overflow-hidden" data-testid="leads-enquiries-panel">
              <div className="px-6 py-4 border-b border-stone-200 flex items-baseline justify-between flex-wrap gap-2">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.25em] text-emerald-700 font-semibold">⚡ Structured Enquiries</div>
                  <div className="text-[11px] text-stone-500 mt-0.5">
                    {enquiries.length} guests filled the full enquiry modal · {withPhone} shared their phone
                  </div>
                </div>
                <div className="text-[10px] text-stone-400 italic">
                  These are higher-intent than raw CTA clicks — guests committed dates + guests + rooms
                </div>
              </div>
              <div className="divide-y divide-stone-100 max-h-[640px] overflow-y-auto">
                {enquiries.map((r, i) => {
                  const guests = (r.adults || 0) + (r.children || 0);
                  const guestsLabel = r.children > 0
                    ? `${r.adults} adult${r.adults === 1 ? '' : 's'} + ${r.children} child${r.children === 1 ? '' : 'ren'}`
                    : `${r.adults} adult${r.adults === 1 ? '' : 's'}`;
                  const fmtDate = (iso) => iso ? new Date(iso + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '—';
                  const followUpMsg = `Hi${r.guest_name ? ` ${r.guest_name}` : ''}, this is GoHimalayan following up on your enquiry for ${r.stay_name} (${fmtDate(r.check_in)} → ${fmtDate(r.check_out)}). Just checking — did you hear back from the host? Happy to help if not.`;
                  return (
                    <div key={i} className="px-6 py-4 hover:bg-amber-50/30">
                      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-1.5">
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-stone-900 text-sm truncate">{r.stay_name || '—'}</div>
                          <div className="text-[11px] text-stone-500 mt-0.5">{fmtTimeAgo(r.created_at)} · {r.source} · {r.utm_source || 'direct'}</div>
                        </div>
                        {r.guest_phone && (
                          <a
                            href={`https://wa.me/${r.guest_phone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(followUpMsg)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[11px] bg-emerald-600 text-white px-3 py-1 rounded-full font-medium hover:bg-emerald-700 flex items-center gap-1"
                            data-testid={`lead-followup-${i}`}
                          >
                            WhatsApp follow-up →
                          </a>
                        )}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1.5 text-xs mt-2">
                        <Field label="Check-in" value={fmtDate(r.check_in)} />
                        <Field label="Check-out" value={fmtDate(r.check_out)} />
                        <Field label="Nights" value={`${r.nights} ${r.nights === 1 ? 'night' : 'nights'}`} />
                        <Field label="Guests" value={`${guests} (${guestsLabel})`} />
                        <Field label="Rooms" value={r.rooms || 1} />
                        <Field label="Name" value={r.guest_name || <span className="text-stone-400 italic">not shared</span>} />
                        <Field label="Phone" value={r.guest_phone ? <span className="font-mono text-stone-900">+{r.guest_phone}</span> : <span className="text-stone-400 italic">not shared</span>} />
                        <Field label="Notes" value={r.note || <span className="text-stone-400 italic">—</span>} className="col-span-2 md:col-span-1" />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Section>
        );
      })()}

      {/* Recent stream */}
      <Section>
        <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-stone-200 text-[11px] uppercase tracking-[0.25em] text-amber-900/60">
            Recent clicks ({data?.recent?.length || 0})
          </div>
          <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-[10px] uppercase tracking-wider text-stone-500">
                <tr>
                  <th className="text-left py-2 px-4">When</th>
                  <th className="text-left py-2 px-4">Stay</th>
                  <th className="text-left py-2 px-4">Source</th>
                  <th className="text-left py-2 px-4">Context</th>
                  <th className="text-left py-2 px-4">UTM</th>
                  <th className="text-left py-2 px-4">Page</th>
                </tr>
              </thead>
              <tbody>
                {(data?.recent || []).map((r, i) => (
                  <tr key={i} className="border-b border-stone-100 hover:bg-amber-50/30">
                    <td className="py-2 px-4 text-stone-600 whitespace-nowrap">{fmtTimeAgo(r.created_at)}</td>
                    <td className="py-2 px-4 text-stone-900 max-w-[200px] truncate" title={r.stay_name}>{r.stay_name || '—'}</td>
                    <td className="py-2 px-4 text-stone-700">{r.source}</td>
                    <td className="py-2 px-4 text-stone-700">{r.context}</td>
                    <td className="py-2 px-4 text-stone-700">{r.utm_source || '(direct)'}</td>
                    <td className="py-2 px-4 text-stone-500 max-w-[200px] truncate" title={r.page}>{r.page}</td>
                  </tr>
                ))}
                {!data?.recent?.length && (
                  <tr><td colSpan={6} className="text-center text-sm text-stone-400 italic py-8">No clicks captured yet. Once a viewer taps a WhatsApp CTA, it'll appear here.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </Section>
    </div>
  );
}
